import {Dropdown, Menu, Tag} from 'antd';
import {
  MoreOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import styles from './TicketCard.module.scss';
import {useNavigate} from 'react-router-dom';
import {shortenTicketId} from '../utils/shortenTicketId';
import {TicketUserAvatar} from './TicketUserAvatar';
import {AwaitingReplyBadge} from './AwaitingReplyBadge';
import {CsatStars, csatTier} from './CsatStars';
import {useTicketActions} from '../useTicketActions';
import UserLink from 'components/userLink/UserLink';

const STATUS_COLORS = {
  GUIDED_FLOW: 'blue',
  AI_ACTIVE: 'purple',
  HUMAN_ACTIVE: 'orange',
  ESCALATED: 'red',
  RESOLVED: 'green',
  ABANDONED: 'default',
};

export const TicketCard = ({ticket, onChanged}) => {
  const navigate = useNavigate();
  const {
    canAssignToMe,
    canResolve,
    assignToMe,
    resolve,
    resolveModal,
    loading,
  } = useTicketActions(ticket, onChanged);

  const getSentimentDot = sentiment => {
    switch (sentiment) {
      case 'positive':
        return <span className={`${styles.dot} ${styles.green}`}></span>;
      case 'negative':
        return <span className={`${styles.dot} ${styles.red}`}></span>;
      default:
        return <span className={`${styles.dot} ${styles.amber}`}></span>;
    }
  };

  const menu = (
    <Menu>
      <Menu.Item
        key="1"
        onClick={e => {
          e.domEvent.stopPropagation();
          navigate(`/dashboard/ai-helpdesk/ticket/${ticket.id}`);
        }}>
        Open Conversation
      </Menu.Item>
      {canAssignToMe && (
        <Menu.Item
          key="2"
          disabled={loading}
          onClick={e => {
            e.domEvent.stopPropagation();
            assignToMe();
          }}>
          Assign to Me
        </Menu.Item>
      )}
      {canResolve && (
        <Menu.Item
          key="5"
          disabled={loading}
          onClick={e => {
            e.domEvent.stopPropagation();
            resolve();
          }}>
          Mark as Resolved
        </Menu.Item>
      )}
    </Menu>
  );

  const csatRowTier = csatTier(ticket.csatScore);
  const isClosed =
    ticket.status === 'RESOLVED' || ticket.status === 'ABANDONED';
  const showCsatRow = ticket.csatScore !== null || isClosed;

  return (
    <div
      className={`${styles.card} ${
        csatRowTier === 'low' ? styles.lowCsatCard : ''
      }`}
      onClick={() => navigate(`/dashboard/ai-helpdesk/ticket/${ticket.id}`)}>
      <div className={styles.header}>
        <div className={styles.userInfo}>
          {getSentimentDot(ticket.sentiment)}
          <div className={styles.userText}>
            {ticket.userId ? (
              <UserLink
                userId={ticket.userId}
                tab="overview"
                className={styles.nameLink}>
                <TicketUserAvatar
                  imageUrl={ticket.userImageUrl}
                  initials={ticket.userInitials}
                  size="sm"
                />{' '}
                {ticket.userDisplayName}
              </UserLink>
            ) : (
              <span
                className={styles.nameLink}
                title="Anonymous ticket — no account linked">
                <TicketUserAvatar
                  imageUrl={ticket.userImageUrl}
                  initials={ticket.userInitials}
                  size="sm"
                />{' '}
                {ticket.userDisplayName}
              </span>
            )}
            <span className={styles.subtleText}>
              {ticket.userSecondaryText}
            </span>
          </div>
        </div>
        <div className={styles.actions} onClick={e => e.stopPropagation()}>
          <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
            <div className={styles.actionBtn}>
              <MoreOutlined />
            </div>
          </Dropdown>
        </div>
      </div>

      <div className={styles.body}>
        <AwaitingReplyBadge awaiting={ticket.awaitingReply} variant="strip" />
        <div className={styles.metaRow}>
          <div className={styles.countryBadge}>
            <GlobalOutlined className={styles.icon} /> {ticket.userCountry}
          </div>
          <div className={styles.languageBadge}>{ticket.userLanguage}</div>
        </div>
        {ticket.intentCategory ? (
          <div className={styles.intentHeadline}>
            <span className={styles.intentLabel}>Issue</span>
            <span className={styles.intentText}>{ticket.intentCategory}</span>
          </div>
        ) : null}
        {ticket.lastMessage ? (
          <div className={styles.lastMessageBlock}>
            <div className={styles.lastMessageBubble}>
              {ticket.lastMessageSender ? (
                <span className={styles.lastMessageSender}>
                  {ticket.lastMessageSender}
                </span>
              ) : null}
              <span className={styles.lastMessageText}>
                {ticket.lastMessage}
              </span>
            </div>
          </div>
        ) : null}
        {ticket.agentAssigned ? (
          <div className={styles.agentRow}>
            Assigned: {ticket.agentAssigned}
          </div>
        ) : null}
        <div className={styles.statusRow}>
          <span className={styles.statusLabel}>Status</span>
          <Tag color={STATUS_COLORS[ticket.status] || 'default'}>
            {ticket.status.replace('_', ' ')}
          </Tag>
          {ticket.isAnonymous ? (
            <Tag
              color="gold"
              title={
                ticket.userId
                  ? 'Originally anonymous — linked to an account'
                  : 'Anonymous — no account linked'
              }>
              {ticket.userId ? 'Anon · Linked' : 'Anonymous'}
            </Tag>
          ) : null}
        </div>
        {showCsatRow ? (
          <div className={styles.statusRow}>
            <span className={styles.statusLabel}>CSAT</span>
            <CsatStars score={ticket.csatScore} size="sm" />
          </div>
        ) : null}
      </div>

      <div className={styles.footer}>
        <span className={styles.timeElapsed}>
          <ClockCircleOutlined className={styles.icon} /> {ticket.timeElapsed}
        </span>
        <span className={styles.modelTag} title={ticket.id}>
          Ticket: {shortenTicketId(ticket.id)}
        </span>
      </div>
      {/* Portal events still bubble through React: keep dialog clicks from
          reaching the card, which opens the ticket. */}
      <span onClick={e => e.stopPropagation()}>{resolveModal}</span>
    </div>
  );
};
